/**
 * Application Configuration
 * Centralized app settings, API configuration, and feature flags
 */

// ============================================================================
// Application Settings
// ============================================================================

export const APP_CONFIG = {
  name: 'MovieApp',
  version: '1.0.0',
  description: 'Your personal movie library and discovery platform',
  author: 'MovieApp Team',
};

// ============================================================================
// Feature Flags
// ============================================================================

export const FEATURES = {
  recommendations: true,
  categories: true,
  watchProviders: true,
  trailers: true,
  posterGallery: true,
  actorPages: true,
  socialSharing: false, // Future feature
  offlineMode: false,   // Future feature
};

// ============================================================================
// Pagination & Limits
// ============================================================================

export const PAGINATION = {
  moviesPerPage: 20,
  actorMoviesPerPage: 12,
  similarMoviesPerPage: 12,
  maxSearchResults: 100,
  initialPage: 1,
};

// ============================================================================
// Performance Settings
// ============================================================================

export const PERFORMANCE = {
  // Debounce delays (ms)
  searchDebounceDelay: 300,
  resizeDebounceDelay: 150,
  scrollDebounceDelay: 100,

  // Cache settings (ms)
  genresCacheDuration: 3600000,      // 1 hour
  movieCacheDuration: 1800000,       // 30 minutes
  searchCacheDuration: 600000,       // 10 minutes

  // Lazy loading
  imageLazyLoadThreshold: '200px',
  componentLazyLoadDelay: 100,
};

// ============================================================================
// UI Settings
// ============================================================================

export const UI_CONFIG = {
  // Animation durations (ms)
  transitionDuration: 300,
  modalAnimationDuration: 200,
  toastDuration: 3000,
  snackbarDuration: 3000,

  // Scroll behavior
  scrollToTopDuration: 400,
  smoothScroll: true,

  // Mobile breakpoint
  mobileBreakpoint: 768,
  tabletBreakpoint: 1024,

  // Grid settings
  movieGridCols: {
    mobile: 2,
    tablet: 3,
    desktop: 4,
    wide: 5,
  },
};

// ============================================================================
// TMDB API Configuration
// ============================================================================

export const TMDB_CONFIG = {
  apiKey: process.env.REACT_APP_TMDB_API_KEY,
  baseUrl: 'https://api.themoviedb.org/3',

  // Image URLs
  imageBaseUrl: 'https://image.tmdb.org/t/p/',

  // Image sizes
  posterSizes: {
    small: 'w185',
    medium: 'w342',
    large: 'w500',
    xlarge: 'w780',
    original: 'original',
  },

  profileSizes: {
    small: 'w185',
    medium: 'w500',
    large: 'h632',
    original: 'original',
  },

  backdropSizes: {
    small: 'w300',
    medium: 'w780',
    large: 'w1280',
    original: 'original',
  },

  logoSizes: {
    small: 'w92',
    medium: 'w154',
    large: 'w185',
    xlarge: 'w300',
    original: 'original',
  },

  // Default sizes for different use cases
  defaultPosterSize: 'w500',
  defaultProfileSize: 'w185',
  defaultBackdropSize: 'w1280',
  defaultLogoSize: 'w154',

  // API limits
  maxPageSize: 20,
  requestTimeout: 10000, // 10 seconds
  maxRetries: 3,
};

// Helper function to build image URLs
export const buildImageUrl = (path, size = 'w500') => {
  if (!path) return null;
  return `${TMDB_CONFIG.imageBaseUrl}${size}${path}`;
};

// Helper to get poster URL
export const getPosterUrl = (path, size = 'medium') => {
  if (!path) return null;
  const sizeValue = TMDB_CONFIG.posterSizes[size] || size;
  return buildImageUrl(path, sizeValue);
};

// Helper to get profile URL
export const getProfileUrl = (path, size = 'small') => {
  if (!path) return null;
  const sizeValue = TMDB_CONFIG.profileSizes[size] || size;
  return buildImageUrl(path, sizeValue);
};

// Helper to get backdrop URL
export const getBackdropUrl = (path, size = 'large') => {
  if (!path) return null;
  const sizeValue = TMDB_CONFIG.backdropSizes[size] || size;
  return buildImageUrl(path, sizeValue);
};

// Validate API key on import
if (!TMDB_CONFIG.apiKey) {
  console.warn('⚠️ TMDB API key is not configured. Set REACT_APP_TMDB_API_KEY in your .env file.');
}

// ============================================================================
// Firebase Configuration (imported from firebase.js)
// ============================================================================

export const FIREBASE_CONFIG = {
  collectionsPrefix: 'prod_', // For multi-environment support
  enablePersistence: true,
  cacheSizeBytes: 40000000, // 40MB
};

// ============================================================================
// Gemini AI Configuration
// ============================================================================

export const GEMINI_CONFIG = {
  apiKey: process.env.REACT_APP_GEMINI_API_KEY,
  model: 'gemini-pro',
  maxTokens: 1000,
  temperature: 0.7,
  enabled: !!process.env.REACT_APP_GEMINI_API_KEY,
};

// Validate Gemini API key
if (FEATURES.recommendations && !GEMINI_CONFIG.apiKey) {
  console.warn('⚠️ Gemini API key is not configured. Recommendations feature will be disabled.');
}

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  genres: 'movieapp_genres',
  searchHistory: 'movieapp_search_history',
  userPreferences: 'movieapp_preferences',
  theme: 'movieapp_theme',
  lastVisited: 'movieapp_last_visited',
};

// ============================================================================
// Routes
// ============================================================================

export const ROUTES = {
  home: '/',
  movie: '/movie/:id',
  actor: '/actor/:id',
  library: '/library',
  auth: '/auth',
  notFound: '*',
};

// ============================================================================
// Validation Rules
// ============================================================================

export const VALIDATION = {
  minSearchLength: 1,
  maxSearchLength: 100,
  minCategoryNameLength: 1,
  maxCategoryNameLength: 50,
  minPasswordLength: 6,
  maxPasswordLength: 128,
};

// ============================================================================
// External Links
// ============================================================================

export const EXTERNAL_LINKS = {
  tmdbWebsite: 'https://www.themoviedb.org',
  imdbWebsite: 'https://www.imdb.com',
  youtubeWatch: 'https://www.youtube.com/watch?v=',
};

// ============================================================================
// Development Settings
// ============================================================================

export const DEV_CONFIG = {
  enableDebugMode: process.env.NODE_ENV === 'development',
  enablePerformanceMonitoring: process.env.NODE_ENV === 'production',
  logLevel: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
  mockApiCalls: false, // Set to true for testing without API
};
